import React, {useCallback, useMemo, useState} from 'react';
import {Box, DialogActions, DialogContent, DialogTitle, Divider} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from '../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../hooks/use-is-mounted';

const formNames = {
    email: 'email',
    phone: 'phone',
};

const SetContactDialog = ({open, customer, onClose, onSaved}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.customers'});
    const isMounted = useIsMounted();
    const [submitting, setSubmitting] = useState(false);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.email]: Yup.string().trim().email(ValidateMessages.incorrectType('email')).nullable(),
        [formNames.phone]: Yup.string().trim().nullable(),
    }).test(
        'at-least-one-contact',
        ValidateMessages.required,
        (values) => Boolean(values?.[formNames.email]?.trim() || values?.[formNames.phone]?.trim()),
    ), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.email]: customer?.email ?? '',
        [formNames.phone]: customer?.phone ?? '',
    }), [customer]);

    /**
     * Saves wallet contact identifiers via mock API.
     */
    const handleSubmit = useCallback(async (values) => {
        if (!customer) {
            return;
        }
        setSubmitting(true);
        const payload = {
            customerNo: customer.customerNo,
            email: values[formNames.email]?.trim() ?? '',
            phone: values[formNames.phone]?.trim() ?? '',
        };
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true, data: payload});
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (response?.resultFlag) {
            onSaved?.(response.data);
            onClose?.();
        }
    }, [customer, isMounted, onClose, onSaved]);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="sm"
        >
            <DialogTitle>
                {t('setContactTitle', {name: customer?.name, customerNo: customer?.customerNo})}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={handleSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'biz-point-set-contact-form'}}
                >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
                        <MuiRFF.TextField
                            name={formNames.email}
                            label={t('email')}
                        />
                        <MuiRFF.TextField
                            name={formNames.phone}
                            label={t('phone')}
                        />
                    </Box>
                </Form>
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="0 8px 0 8px"
                    onClick={onClose}
                    disabled={submitting}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="8px 0 8px 0"
                    type="submit"
                    form="biz-point-set-contact-form"
                    disabled={submitting}
                >
                    {t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SetContactDialog;
